import { useState, useEffect, useCallback } from "react";
import { Modal, Fade, Grid, Card } from "@mui/material";
import { keyframes, styled } from "@mui/system";
import { useTranslation } from "react-i18next";
import { MDTypography, MDButton } from "components";
import { EditorService, AdditionalDocumentsService, DocumentsService } from "services";
import { InitializeEditor, PassDataToEditor } from "@inteligencijadev/html-editor/dist/bundle";
import { useSnackbar } from "helper";
import recoverMissingFontFacesInHtml from "helper/recoverMissingFontFacesInHtml";
import signalRHubConnectionUtility from "helper/signalRHubConnectionUtility";
import axios from "axios";

const CANCEL_TOKEN = axios.CancelToken;
let SOURCE = CANCEL_TOKEN.source();

const TYPE_CONVERTED_HTML = "convertedHtml";
const STATUS_SUCCESS = "success";
const STATUS_FAIL = "fail";
const PROJECTS_HUB_REGISTER_HANDLER = "AdditionalDocumentReady";
const REPORT_TAG_PREFIX = "report-";

const DIFF_KEYS = [
  "addedReports",
  "deletedReportShortGuids",
  "deletedReportCellValueLocations",
  "updatedReportCellValuesByValueLocation",
  "deletedFootnoteSpanIds",
  "deletedNoteTagSpanIds",
];

const OPTIONAL_ADDED_REPORT_CELL_KEYS = [
  "backgroundColor",
  "textColor",
  "fontSize",
  "indent",
  "horizontalAlignment",
  "verticalAlignment",
  "wrapText",
  "borderTop",
  "borderBottom",
  "borderLeft",
  "borderRight",
  "height",
  "width",
  "fontFamily",
  "fontBold",
  "fontItalic",
  "fontUnderlineType",
];

const uploadHtmlSaveQueueByProject = new Map();
const knownReportShortGuidsByClientKeyByProject = new Map();

/**
 * @typedef {Object} UploadHtmlAddedReportCellRequest
 * @property {number} rowIndex
 * @property {number} columnIndex
 * @property {string} value
 * @property {number|null} periodOffset
 * @property {string} valueLocation
 * @property {boolean} isHeader
 * @property {boolean} isRowHeader
 * @property {boolean} isColumnHeader
 */

/**
 * @typedef {Object} UploadHtmlAddedReportRequest
 * @property {string} clientReportKey
 * @property {string} name
 * @property {string|null} description
 * @property {string} reportTypeShortGuid
 * @property {UploadHtmlAddedReportCellRequest[]} reportCells
 */

/**
 * @typedef {Object} UploadHtmlDiffRequest
 * @property {UploadHtmlAddedReportRequest[]=} addedReports
 * @property {string[]=} deletedReportShortGuids
 * @property {string[]=} deletedReportCellValueLocations
 * @property {Object<string, string>=} updatedReportCellValuesByValueLocation
 * @property {string[]=} deletedFootnoteSpanIds
 * @property {string[]=} deletedNoteTagSpanIds
 */

/**
 * @typedef {Object} UploadHtmlResponseData
 * @property {boolean=} reportDeleted
 * @property {Object<string, string>=} addedReportShortGuidsByClientReportKey
 */

const cleanString = (value) => (typeof value === "string" ? value.trim() : "");

const normalizeValueLocation = (valueLocation) => {
  if (typeof valueLocation !== "string" || valueLocation.trim() === "") return "";
  const parts = valueLocation.split("#");
  return parts[parts.length - 1].trim();
};

const uniq = (arr = []) => [
  ...new Set(
    arr
      .filter((item) => typeof item === "string")
      .map((item) => item.trim())
      .filter(Boolean)
  ),
];

const toNullableNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const toNullableValue = (value) => (value === undefined ? null : value);

const toIntegerOrNull = (value) => {
  const parsedValue = Number(value);
  if (!Number.isInteger(parsedValue)) return null;
  return parsedValue;
};

const sanitizeClientReportKeyForValueLocation = (clientReportKey) =>
  clientReportKey.replace(/[^a-zA-Z0-9_-]/g, "_");

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const getTemporaryReportTagCandidates = (clientReportKey) => {
  const cleanClientReportKey = cleanString(clientReportKey);
  if (cleanClientReportKey === "") return [];

  const prefixedToken = cleanClientReportKey.startsWith(REPORT_TAG_PREFIX)
    ? cleanClientReportKey
    : `${REPORT_TAG_PREFIX}${cleanClientReportKey}`;

  return [...new Set([cleanClientReportKey, prefixedToken])];
};

const replaceClassTokenInHtml = (htmlContent, oldToken, newToken) => {
  if (oldToken === "" || oldToken === newToken) return htmlContent;

  return htmlContent.replace(
    /\bclass\s*=\s*("([^"]*)"|'([^']*)')/gi,
    (attributeMatch, quotedValue, doubleQuotedClasses, singleQuotedClasses) => {
      const classValue =
        typeof doubleQuotedClasses === "string" ? doubleQuotedClasses : singleQuotedClasses || "";
      if (!classValue.includes(oldToken)) return attributeMatch;

      const classTokens = classValue.split(/\s+/).filter(Boolean);
      if (!classTokens.includes(oldToken)) return attributeMatch;

      const updatedClassTokens = [
        ...new Set(
          classTokens.map((classToken) => {
            if (classToken === oldToken) {
              return newToken;
            }

            return classToken;
          })
        ),
      ];
      const quote = quotedValue.startsWith("'") ? "'" : '"';

      return `class=${quote}${updatedClassTokens.join(" ")}${quote}`;
    }
  );
};

const replaceIdTokenInHtml = (htmlContent, oldToken, newToken) => {
  if (oldToken === "" || oldToken === newToken) return htmlContent;

  const escapedOldToken = escapeRegExp(oldToken);
  return htmlContent
    .replace(new RegExp(`\\bid\\s*=\\s*"${escapedOldToken}"`, "g"), `id="${newToken}"`)
    .replace(new RegExp(`\\bid\\s*=\\s*'${escapedOldToken}'`, "g"), `id='${newToken}'`);
};

const normalizeHtmlReportTags = (htmlContent, reportShortGuidByClientKey = {}) => {
  if (typeof htmlContent !== "string" || htmlContent.trim() === "") return htmlContent;

  const entries = Object.entries(reportShortGuidByClientKey || {});
  if (entries.length === 0) return htmlContent;

  let normalizedHtml = htmlContent;

  entries.forEach(([rawClientReportKey, rawReportShortGuid]) => {
    const clientReportKey = cleanString(rawClientReportKey);
    const reportShortGuid = cleanString(rawReportShortGuid);
    if (clientReportKey === "" || reportShortGuid === "") return;

    const finalReportTag = `${REPORT_TAG_PREFIX}${reportShortGuid}`;

    getTemporaryReportTagCandidates(clientReportKey).forEach((temporaryToken) => {
      normalizedHtml = replaceClassTokenInHtml(normalizedHtml, temporaryToken, finalReportTag);
      normalizedHtml = replaceIdTokenInHtml(normalizedHtml, temporaryToken, finalReportTag);
    });
  });

  return normalizedHtml;
};

const enqueueUploadHtmlSaveByProject = (projectGuid, task) => {
  const previousTask = uploadHtmlSaveQueueByProject.get(projectGuid) || Promise.resolve();

  const queuedTask = previousTask.catch(() => {}).then(task);
  uploadHtmlSaveQueueByProject.set(projectGuid, queuedTask);

  queuedTask.finally(() => {
    if (uploadHtmlSaveQueueByProject.get(projectGuid) === queuedTask) {
      uploadHtmlSaveQueueByProject.delete(projectGuid);
    }
  });

  return queuedTask;
};

const getKnownReportShortGuidMappings = (projectGuid) =>
  knownReportShortGuidsByClientKeyByProject.get(projectGuid) || {};

const setKnownReportShortGuidMappings = (projectGuid, mappings) => {
  if (Object.keys(mappings).length === 0) {
    knownReportShortGuidsByClientKeyByProject.delete(projectGuid);
    return;
  }

  knownReportShortGuidsByClientKeyByProject.set(projectGuid, mappings);
};

const sanitizeAddedReportCell = (rawCell, clientReportKey) => {
  const rowIndex = toIntegerOrNull(rawCell?.rowIndex);
  const columnIndex = toIntegerOrNull(rawCell?.columnIndex);
  if (rowIndex === null || columnIndex === null || rowIndex < 0 || columnIndex < 0) return null;

  const cleanClientReportKey = sanitizeClientReportKeyForValueLocation(clientReportKey);
  const fallbackValueLocation = `cell_${cleanClientReportKey}_r${rowIndex}_c${columnIndex}`;
  const normalizedValueLocation = normalizeValueLocation(cleanString(rawCell?.valueLocation));

  const cell = {
    rowIndex,
    columnIndex,
    value: rawCell?.value === null || rawCell?.value === undefined ? "" : String(rawCell.value),
    periodOffset: toNullableNumber(rawCell?.periodOffset),
    valueLocation: normalizedValueLocation || fallbackValueLocation,
    isHeader: Boolean(rawCell?.isHeader),
    isRowHeader: Boolean(rawCell?.isRowHeader),
    isColumnHeader: Boolean(rawCell?.isColumnHeader),
  };

  OPTIONAL_ADDED_REPORT_CELL_KEYS.forEach((key) => {
    cell[key] = toNullableValue(rawCell?.[key]);
  });

  return cell;
};

const sanitizeAddedReports = (rawAddedReports, knownReportShortGuidByClientKey = {}) => {
  if (!Array.isArray(rawAddedReports)) return [];

  const alreadyMappedClientKeys = new Set(
    Object.keys(knownReportShortGuidByClientKey).map(cleanString).filter(Boolean)
  );

  const seenClientReportKeys = new Set();

  return rawAddedReports.reduce((addedReports, rawReport, reportIndex) => {
    const clientReportKey = cleanString(rawReport?.clientReportKey);
    const name = cleanString(rawReport?.name);
    const reportTypeShortGuid = cleanString(rawReport?.reportTypeShortGuid);

    if (!clientReportKey) {
      throw new Error(`Added report at index ${reportIndex} is missing clientReportKey.`);
    }

    if (alreadyMappedClientKeys.has(clientReportKey)) {
      return addedReports;
    }

    if (seenClientReportKeys.has(clientReportKey)) {
      throw new Error(`Duplicate clientReportKey detected: ${clientReportKey}.`);
    }
    seenClientReportKeys.add(clientReportKey);

    if (!name) {
      throw new Error(`Added report ${clientReportKey} is missing name.`);
    }

    if (!reportTypeShortGuid) {
      throw new Error(`Added report ${clientReportKey} is missing reportTypeShortGuid.`);
    }

    const seenCoordinates = new Set();
    const reportCells = (Array.isArray(rawReport?.reportCells) ? rawReport.reportCells : [])
      .map((rawCell) => sanitizeAddedReportCell(rawCell, clientReportKey))
      .filter((cell) => {
        if (cell == null) return false;
        const coordinateKey = `${cell.rowIndex}:${cell.columnIndex}`;
        if (seenCoordinates.has(coordinateKey)) return false;

        seenCoordinates.add(coordinateKey);
        return true;
      });

    if (reportCells.length === 0) {
      throw new Error(`Added report ${clientReportKey} has no valid reportCells.`);
    }

    addedReports.push({
      clientReportKey,
      name,
      description:
        rawReport?.description === null || rawReport?.description === undefined
          ? null
          : String(rawReport.description),
      reportTypeShortGuid,
      reportCells,
    });

    return addedReports;
  }, []);
};

const sanitizeDiff = (rawDiff = {}, knownReportShortGuidByClientKey = {}) => {
  const addedReports = sanitizeAddedReports(rawDiff?.addedReports, knownReportShortGuidByClientKey);
  const deletedReportShortGuids = uniq(rawDiff?.deletedReportShortGuids);
  const deletedReportCellValueLocations = uniq(
    (rawDiff?.deletedReportCellValueLocations || []).map(normalizeValueLocation).filter(Boolean)
  );
  const deletedFootnoteSpanIds = uniq(rawDiff?.deletedFootnoteSpanIds);
  const deletedNoteTagSpanIds = uniq(rawDiff?.deletedNoteTagSpanIds);

  const deletedCellsSet = new Set(deletedReportCellValueLocations);
  const updatedReportCellValuesByValueLocation = {};

  Object.entries(rawDiff?.updatedReportCellValuesByValueLocation || {}).forEach(
    ([rawKey, value]) => {
      if (typeof value !== "string") return;
      const key = normalizeValueLocation(rawKey);
      if (!key || deletedCellsSet.has(key)) return;
      updatedReportCellValuesByValueLocation[key] = value;
    }
  );

  const hasUpdatedCellValues = Object.keys(updatedReportCellValuesByValueLocation).length > 0;

  const diff = {
    ...(addedReports.length > 0 ? { addedReports } : {}),
    ...(deletedReportShortGuids.length > 0 ? { deletedReportShortGuids } : {}),
    ...(deletedReportCellValueLocations.length > 0 ? { deletedReportCellValueLocations } : {}),
    ...(hasUpdatedCellValues ? { updatedReportCellValuesByValueLocation } : {}),
    ...(deletedFootnoteSpanIds.length > 0 ? { deletedFootnoteSpanIds } : {}),
    ...(deletedNoteTagSpanIds.length > 0 ? { deletedNoteTagSpanIds } : {}),
  };

  return Object.keys(diff).length > 0 ? diff : undefined;
};

const getRawDiffFromSaveEvent = (saveData = {}) => {
  if (saveData?.diff && typeof saveData.diff === "object") return saveData.diff;

  const diffFromTopLevel = DIFF_KEYS.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(saveData, key)) {
      acc[key] = saveData[key];
    }
    return acc;
  }, {});

  return Object.keys(diffFromTopLevel).length > 0 ? diffFromTopLevel : undefined;
};

const extractAddedReportShortGuidMappings = (responseData = {}) => {
  const rawMapping = responseData?.addedReportShortGuidsByClientReportKey;
  if (rawMapping == null || typeof rawMapping !== "object") return {};

  return Object.entries(rawMapping).reduce((acc, [clientReportKey, reportShortGuid]) => {
    const cleanClientReportKey = cleanString(clientReportKey);
    const cleanReportShortGuid = cleanString(reportShortGuid);
    if (!cleanClientReportKey || !cleanReportShortGuid) return acc;

    acc[cleanClientReportKey] = cleanReportShortGuid;
    return acc;
  }, {});
};

const buildUploadHtmlRequest = (saveData = {}, knownReportShortGuidByClientKey = {}) => {
  let rawHtmlContent = null;

  if (typeof saveData?.htmlContent === "string") {
    rawHtmlContent = saveData.htmlContent;
  } else if (typeof saveData?.html === "string") {
    rawHtmlContent = saveData.html;
  }

  if (rawHtmlContent === null) {
    throw new Error("Missing html content in editor save payload");
  }

  const htmlContent = normalizeHtmlReportTags(rawHtmlContent, knownReportShortGuidByClientKey);
  const rawDiff = getRawDiffFromSaveEvent(saveData);
  const diff = rawDiff ? sanitizeDiff(rawDiff, knownReportShortGuidByClientKey) : undefined;

  return {
    htmlContent,
    ...(diff ? { diff } : {}),
    skipFullReconciliation: false,
  };
};

function EditDocumentModal({ isOpen, projectShortGuid, actionAfterSave = async () => {}, close }) {
  const [loader, setLoader] = useState({ isLoading: false, label: "" });
  const [hasDocBeenSaved, setHasDocBeenSaved] = useState(false);
  const { startEditor: startEditorApiCall } = EditorService();
  const { getAdditionalDocument, additionalDocuments } = AdditionalDocumentsService();
  const { uploadHtml } = DocumentsService();
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar();

  const save = async ({ data: saveData }) => {
    await enqueueUploadHtmlSaveByProject(projectShortGuid, async () => {
      try {
        setLoader(() => ({ isLoading: true, label: "savingdocument" }));

        const knownMappings = getKnownReportShortGuidMappings(projectShortGuid);
        const request = buildUploadHtmlRequest(saveData, knownMappings);

        const { status, data } = await uploadHtml(projectShortGuid, request, {
          cancelToken: SOURCE.token,
        });

        if (status === 200) {
          const addedReportShortGuidMappings = extractAddedReportShortGuidMappings(data);
          const hasAddedReportShortGuidMappings =
            Object.keys(addedReportShortGuidMappings).length > 0;

          if (hasAddedReportShortGuidMappings) {
            const mergedMappings = {
              ...knownMappings,
              ...addedReportShortGuidMappings,
            };
            setKnownReportShortGuidMappings(projectShortGuid, mergedMappings);

            const normalizedHtml = normalizeHtmlReportTags(request.htmlContent, mergedMappings);
            await uploadHtml(
              projectShortGuid,
              {
                htmlContent: normalizedHtml,
                skipFullReconciliation: true,
              },
              { cancelToken: SOURCE.token }
            );
          }

          showSuccessSnackbar({ content: "projects.details.reports.documentsaved" });
          setHasDocBeenSaved(true);
        }
      } catch (e) {
        if (!axios.isCancel(e))
          showErrorSnackbar({
            content: e?.response?.data?.errorTranslation || e?.message || "unhandlederror",
          });
      } finally {
        setLoader(() => ({ isLoading: false, label: "" }));
      }
    });
  };

  const handleAdditionalDocumentReady = async (hubInstance, message) => {
    try {
      // message example: ADDITIONAL_DOC_READY {projectShortGuid} {documentId} {nonSpaceDelimitedDateTime}
      const documentId = message.split(" ")[2];
      const {
        status,
        data: { htmlFile: html },
      } = await getAdditionalDocument(documentId);
      if (status === 200)
        PassDataToEditor({
          type: TYPE_CONVERTED_HTML,
          html,
          status: STATUS_SUCCESS,
          id: documentId,
        });
    } catch (e) {
      PassDataToEditor({
        type: TYPE_CONVERTED_HTML,
        status: STATUS_FAIL,
        message: e?.response?.data?.errorTranslation,
      });
    } finally {
      hubInstance.dispose();
    }
  };

  const convertToHtml = async ({ data: { file } }) => {
    try {
      const { status } = await additionalDocuments(projectShortGuid, file);
      if (status === 200) {
        const projectsHub = signalRHubConnectionUtility("projectsHub");
        projectsHub.registerHandler(PROJECTS_HUB_REGISTER_HANDLER, (message) =>
          handleAdditionalDocumentReady(projectsHub, message)
        );
        projectsHub.start();
      }
    } catch (e) {
      PassDataToEditor({
        type: TYPE_CONVERTED_HTML,
        status: STATUS_FAIL,
        message: e?.response?.data?.errorTranslation,
      });
    }
  };

  const getConvertedHtml = async ({ data: { id } }) => {
    try {
      const {
        status,
        data: { htmlFile: html },
      } = await getAdditionalDocument(id);
      if (status === 200)
        PassDataToEditor({ type: TYPE_CONVERTED_HTML, html, status: STATUS_SUCCESS });
    } catch (e) {
      PassDataToEditor({
        type: TYPE_CONVERTED_HTML,
        status: STATUS_FAIL,
        message: e?.response?.data?.errorTranslation,
      });
    }
  };

  const handleMessage = useCallback((event) => {
    switch (event.data.type) {
      case "save":
        save(event);
        break;
      case "convertToHtml":
        convertToHtml(event);
        break;
      case "getConvertedHtml":
        getConvertedHtml(event);
        break;
      case "close":
        close();
        break;
      default:
    }
  }, []);

  const initializeEditorWhenContainerReady = (
    { html, convertedFiles, reports, noteTags, footnotes },
    attempt = 0
  ) => {
    const editorContainer = document.getElementById("editor-container");
    const isContainerVisible = editorContainer != null && editorContainer.offsetHeight > 0;

    if (!isContainerVisible) {
      if (attempt < 20) {
        requestAnimationFrame(() =>
          initializeEditorWhenContainerReady(
            { html, convertedFiles, reports, noteTags, footnotes },
            attempt + 1
          )
        );
      }
      return;
    }

    if (editorContainer.childNodes && editorContainer.childNodes.length === 0) {
      InitializeEditor(editorContainer, html, convertedFiles, reports, noteTags, footnotes);
      window.addEventListener("message", handleMessage);
    }
  };
  const startEditor = async () => {
    try {
      setLoader(() => ({ isLoading: true, label: "loadingdocument" }));
      const {
        status,
        data: { html, convertedFiles, reports, noteTags, footnotes },
      } = await startEditorApiCall({ projectShortGuid }, { cancelToken: SOURCE.token });
      if (status === 200) {
        const hydratedHtml = await recoverMissingFontFacesInHtml({
          htmlContent: html,
          convertedFiles,
          getAdditionalDocument,
        });

        setLoader(() => ({ isLoading: false, label: "" }));
        requestAnimationFrame(() =>
          initializeEditorWhenContainerReady({
            html: hydratedHtml,
            convertedFiles,
            reports,
            noteTags,
            footnotes,
          })
        );
      }
    } catch (e) {
      if (!axios.isCancel(e))
        showErrorSnackbar({ content: e?.response?.data?.errorTranslation || "unhandlederror" });
      close();
    } finally {
      setLoader(() => ({ isLoading: false, label: "" }));
    }
  };

  const handleAbort = () => {
    SOURCE.cancel();
    SOURCE = CANCEL_TOKEN.source();
  };

  const handleClose = async () => {
    if (hasDocBeenSaved) {
      await actionAfterSave();
      setHasDocBeenSaved(false);
    }
    window.removeEventListener("message", handleMessage);
  };

  useEffect(() => {
    if (isOpen) startEditor();
    else handleClose();
  }, [isOpen]);

  return (
    <Modal open={isOpen}>
      <Fade in={isOpen} timeout={50}>
        <Grid container justifyContent="center" alignItems="center">
          <Grid item>
            <Card sx={{ height: "100vh", width: "100vw" }}>
              {loader.isLoading ? (
                <EditDocumentLoader label={loader.label} cancel={handleAbort} />
              ) : null}
              <div id="editor-container" style={{ height: loader.isLoading ? "0vh" : "100vh" }} />
            </Card>
          </Grid>
        </Grid>
      </Fade>
    </Modal>
  );
}

function EditDocumentLoader({ label, cancel }) {
  const { t } = useTranslation();

  const dotAnimation = keyframes`
    0%, 80%, 100% { opacity: 0; }
    40% { opacity: 1; }
  `;

  const Dot = styled("span")(() => ({
    animation: `${dotAnimation} 1.2s infinite`,
  }));

  const DOTS = ["DOT-1", "DOT-2", "DOT-3"];

  return (
    <Grid
      container
      alignItems="center"
      direction="column"
      justifyContent="center"
      style={{ height: "100%" }}
    >
      <Grid item>
        <MDTypography variant="h3">
          {t(`projects.details.reports.${label}`)}
          {DOTS.map((i, idx) => (
            <Dot key={i} style={{ animationDelay: `${0.2 * idx}s` }}>
              .
            </Dot>
          ))}
        </MDTypography>
      </Grid>
      <Grid item pt={5}>
        <MDButton variant="gradient" color="dark" onClick={cancel}>
          {t("cancel")}
        </MDButton>
      </Grid>
    </Grid>
  );
}

export default EditDocumentModal;
